import React, { forwardRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Badge, Theme, Pattern, SocialHandles } from '../types';
import { cn } from '../lib/utils';
import * as LucideIcons from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

interface BannerPreviewProps {
  theme: Theme;
  pattern?: Pattern;
  selectedBadges: Badge[];
  userName: string;
  userTitle: string;
  scale?: number;
  showQrCode?: boolean;
  showProfilePic?: boolean;
  profilePicSrc?: string;
  profileUrl?: string;
  layout?: 'compact' | 'centric';
  fontFamily?: 'sans' | 'display' | 'mono' | 'serif';
  showSocials?: boolean;
  socialHandles?: SocialHandles;
  customImageUrl?: string;
  isGeneratingAi?: boolean;
  bannerSize?: 'horizontal' | 'square' | 'vertical';
  onReorderBadges?: (sourceId: string, targetId: string) => void;
}

export const BannerPreview = forwardRef<HTMLDivElement, BannerPreviewProps>(
  ({ theme, pattern, selectedBadges, userName, userTitle, scale = 1, showQrCode, showProfilePic, profilePicSrc, profileUrl, layout = 'compact', fontFamily = 'sans', showSocials, socialHandles, customImageUrl, isGeneratingAi, bannerSize = 'horizontal', onReorderBadges }, ref) => {
    const [draggedBadgeId, setDraggedBadgeId] = useState<string | null>(null);

    const handleDragStart = (e: React.DragEvent, id: string) => {
      if (!onReorderBadges) return;
      e.dataTransfer.setData('text/plain', id);
      setDraggedBadgeId(id);
    };

    const handleDragOver = (e: React.DragEvent) => {
      if (!onReorderBadges) return;
      e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent, targetId: string) => {
      if (!onReorderBadges) return;
      e.preventDefault();
      const sourceId = e.dataTransfer.getData('text/plain');
      if (sourceId && sourceId !== targetId) {
        onReorderBadges(sourceId, targetId);
      }
      setDraggedBadgeId(null);
    };

    const handleDragEnd = () => {
      setDraggedBadgeId(null);
    };

    const badgeCount = selectedBadges.length;
    const getBadgeStyles = (count: number, size: string) => {
      const isSq = size === 'square';
      const isVert = size === 'vertical';
      if (count <= 6) {
        return {
          containerGap: isVert ? 'gap-4' : isSq ? 'gap-4' : 'gap-3',
          padding: isVert ? 'px-6 py-3' : isSq ? 'px-5 py-2.5' : 'px-4 py-2',
          textSize: isVert ? 'text-[24px]' : isSq ? 'text-[22px]' : 'text-[16px]',
          imgSize: isVert ? 'w-12 h-12' : isSq ? 'w-10 h-10' : 'w-8 h-8',
          iconSize: isVert ? 'w-8 h-8' : isSq ? 'w-7 h-7' : 'w-6 h-6'
        };
      } else if (count <= 12) {
        return {
          containerGap: isVert ? 'gap-3' : isSq ? 'gap-2' : 'gap-2',
          padding: isVert ? 'px-4 py-2' : isSq ? 'px-4 py-2' : 'px-3 py-1.5',
          textSize: isVert ? 'text-[18px]' : isSq ? 'text-[18px]' : 'text-[14px]',
          imgSize: isVert ? 'w-8 h-8' : isSq ? 'w-8 h-8' : 'w-6 h-6',
          iconSize: isVert ? 'w-6 h-6' : isSq ? 'w-6 h-6' : 'w-5 h-5'
        };
      } else if (count <= 20) {
        return {
          containerGap: isVert ? 'gap-2' : isSq ? 'gap-2' : 'gap-1.5',
          padding: isVert ? 'px-3 py-1.5' : isSq ? 'px-3 py-1.5' : 'px-2.5 py-1',
          textSize: isVert ? 'text-[16px]' : isSq ? 'text-[14px]' : 'text-[12px]',
          imgSize: isVert ? 'w-6 h-6' : isSq ? 'w-6 h-6' : 'w-5 h-5',
          iconSize: isVert ? 'w-5 h-5' : isSq ? 'w-5 h-5' : 'w-4 h-4'
        };
      } else {
        return {
          containerGap: isVert ? 'gap-1.5' : isSq ? 'gap-1.5' : 'gap-1',
          padding: isVert ? 'px-2.5 py-1' : isSq ? 'px-2.5 py-1' : 'px-2 py-0.5',
          textSize: isVert ? 'text-[12px]' : isSq ? 'text-[12px]' : 'text-[10px]',
          imgSize: isVert ? 'w-5 h-5' : isSq ? 'w-5 h-5' : 'w-4 h-4',
          iconSize: isVert ? 'w-4 h-4' : isSq ? 'w-4 h-4' : 'w-3 h-3'
        };
      }
    };
    
    const badgeStyles = getBadgeStyles(badgeCount, bannerSize);

    const dimensions = {
      horizontal: { width: '1584px', aspectRatio: '4/1' },
      square: { width: '1080px', aspectRatio: '1/1' },
      vertical: { width: '1080px', aspectRatio: '9/16' }
    }[bannerSize];

    return (
      <div 
        className="absolute top-0 left-0 origin-top-left"
        style={{
          transform: `scale(${scale})`,
        }}
      >
        <div 
          id="banner-capture-area"
          ref={ref}
          className="relative overflow-hidden transition-all duration-500 ease-in-out"
          style={dimensions}
        >
          {isGeneratingAi && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm text-white">
              <LucideIcons.Loader2 className="w-16 h-16 animate-spin mb-4" />
              <h3 className="text-3xl font-bold tracking-tight">Generating AI Background...</h3>
              <p className="text-lg opacity-80 mt-2">This usually takes about 10-15 seconds.</p>
            </div>
          )}
          {customImageUrl && (
            <div 
              className="absolute inset-0 z-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${customImageUrl})` }}
            />
          )}
          <div
            className={cn(
              'relative flex flex-col z-10 transition-all duration-500 ease-in-out h-full',
              !customImageUrl && theme.backgroundClass,
              theme.textClass
            )}
            style={{ width: dimensions.width, aspectRatio: dimensions.aspectRatio }}
          >
          {/* Base Gradients based on theme */}
          {!customImageUrl && theme.id === 'orbit-dark' && (

            <>
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-800 via-zinc-950 to-black"></div>
              <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-zinc-500/10 rounded-full blur-[120px] -tranzinc-y-1/2 tranzinc-x-1/3"></div>
            </>
          )}
          {!customImageUrl && theme.id === 'cloud-light' && (
            <>
              <div className="absolute inset-0 bg-white"></div>
              <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-zinc-100 rounded-full blur-[100px]"></div>
              <div className="absolute top-0 right-20 w-[400px] h-[400px] bg-zinc-100 rounded-full blur-[100px]"></div>
            </>
          )}
          {!customImageUrl && theme.id === 'hacker' && (
            <>
              <div className="absolute inset-0 bg-black"></div>
            </>
          )}
          {!customImageUrl && theme.id === 'midnight-purple' && (
            <>
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-zinc-800 via-zinc-900 to-black"></div>
            </>
          )}

          {/* Abstract Patterns based on selected pattern */}
          {!customImageUrl && pattern?.id === 'grid' && (
            <div className="absolute inset-0 opacity-20">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="gridPattern" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#gridPattern)" />
              </svg>
            </div>
          )}
          {!customImageUrl && pattern?.id === 'dots' && (
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(currentColor_1px,transparent_1px)] [background-size:24px_24px]"></div>
          )}
          {!customImageUrl && pattern?.id === 'waves' && (
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="wavePattern" width="100" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 0 20 Q 25 0 50 20 T 100 20" fill="none" stroke="currentColor" strokeWidth="2"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#wavePattern)" />
              </svg>
            </div>
          )}
          {!customImageUrl && pattern?.id === 'glow' && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-white rounded-full mix-blend-overlay filter blur-[150px] opacity-20 animate-pulse"></div>
              <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] bg-white rounded-full mix-blend-overlay filter blur-[200px] opacity-10"></div>
            </div>
          )}
          {!customImageUrl && pattern?.id === 'geometric' && (
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="geoPattern" width="100" height="100" patternUnits="userSpaceOnUse">
                    <polygon points="50,0 100,50 50,100 0,50" fill="none" stroke="currentColor" strokeWidth="1"/>
                    <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="1"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#geoPattern)" />
              </svg>
            </div>
          )}

          {/* Main Content Container */}
          <div className={cn("relative z-10 flex-1 flex flex-col w-full", bannerSize === 'horizontal' ? "p-8" : "p-12")}>
            {/* Top Right: Brand & QR */}
            <div className={cn("absolute z-20 flex items-center gap-6", bannerSize === 'horizontal' ? "top-8 right-8" : "top-12 right-12")}>
              {showQrCode && profileUrl && (
                <div className={cn("bg-white shadow-2xl", bannerSize === 'horizontal' ? "p-3 rounded-2xl" : "p-5 rounded-3xl")}>
                  <QRCodeSVG 
                    value={profileUrl} 
                    size={bannerSize === 'horizontal' ? 64 : 120}
                    level="L"
                    includeMargin={false}
                    className="rounded-lg"
                  />
                </div>
              )}
              {/* Logo / Brand Watermark */}
              <div className={cn("flex items-center opacity-90 bg-black/20 border border-white/10 shadow-2xl backdrop-blur-sm", bannerSize === 'horizontal' ? "px-3 py-3 rounded-2xl" : "px-5 py-5 rounded-3xl")}>
                <img 
                  src="/logo.png" 
                  alt="Orbit of Ops" 
                  className={cn("object-contain drop-shadow-lg", bannerSize === 'horizontal' ? "h-16" : "h-[120px]")} 
                  crossOrigin="anonymous" 
                />
              </div>
            </div>

            {layout === 'centric' ? (
              <div className={cn("flex-1 flex flex-col items-center justify-center w-full", bannerSize === 'horizontal' ? "gap-3" : bannerSize === 'square' ? "gap-6" : "gap-8 pt-12")}>
                {/* User Info */}
                <div className="flex flex-col items-center text-center max-w-[1000px] shrink-0">
                  {showProfilePic && profilePicSrc && (
                    <div className={cn("rounded-full overflow-hidden border-4 border-white/20 shadow-2xl mb-2", 
                      bannerSize === 'horizontal' ? "w-20 h-20" : bannerSize === 'square' ? "w-40 h-40" : "w-48 h-48"
                    )}>
                      <img src={profilePicSrc} alt="Profile" className="w-full h-full object-cover" crossOrigin="anonymous" />
                    </div>
                  )}
                  <h1 className={cn(
                    "leading-[1.1] font-black tracking-tighter mb-1 drop-shadow-xl",
                    bannerSize === 'horizontal' ? (showProfilePic ? "text-[40px]" : "text-[56px]") : bannerSize === 'square' ? "text-[80px]" : "text-[100px]"
                  )} style={{ fontFamily: `var(--font-${fontFamily})` }}>
                    {userName || 'Your Name'}
                  </h1>
                  <p className={cn(
                    "font-medium opacity-90 leading-snug drop-shadow-lg",
                    bannerSize === 'horizontal' ? (showProfilePic ? "text-[20px]" : "text-[24px]") : bannerSize === 'square' ? "text-[32px]" : "text-[40px]"
                  )} style={{ fontFamily: `var(--font-${fontFamily === 'display' ? 'sans' : fontFamily})` }}>
                    {userTitle || 'Cloud Learner & Technologist'}
                  </p>
                  
                  {/* Social Links Centric */}
                  {showSocials && socialHandles && (
                    <div className={cn(
                      "flex items-center",
                      bannerSize === 'horizontal' ? "gap-4 mt-2" : "gap-8 mt-6"
                    )}>
                      {socialHandles.linkedin && (
                        <div className={cn("flex items-center font-medium opacity-90 drop-shadow-md", bannerSize === 'horizontal' ? "gap-2 text-xl" : "gap-4 text-2xl")}>
                          <LucideIcons.Linkedin className={bannerSize === 'horizontal' ? "w-5 h-5" : "w-8 h-8"} />
                          <span>{socialHandles.linkedin}</span>
                        </div>
                      )}
                      {socialHandles.github && (
                        <div className={cn("flex items-center font-medium opacity-90 drop-shadow-md", bannerSize === 'horizontal' ? "gap-2 text-xl" : "gap-4 text-2xl")}>
                          <LucideIcons.Github className={bannerSize === 'horizontal' ? "w-5 h-5" : "w-8 h-8"} />
                          <span>{socialHandles.github}</span>
                        </div>
                      )}
                      {socialHandles.twitter && (
                        <div className={cn("flex items-center font-medium opacity-90 drop-shadow-md", bannerSize === 'horizontal' ? "gap-2 text-xl" : "gap-4 text-2xl")}>
                          <LucideIcons.Twitter className={bannerSize === 'horizontal' ? "w-5 h-5" : "w-8 h-8"} />
                          <span>{socialHandles.twitter}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Badges */}
                <div className={cn("flex items-center justify-center flex-wrap w-full px-2 shrink", badgeStyles.containerGap)}>
                  {selectedBadges.length === 0 && (
                    <div className="text-2xl opacity-50 italic font-medium">Select badges to display here...</div>
                  )}
                  
                  <AnimatePresence>
                    {selectedBadges.map((badge) => {
                      const isGlow = theme.badgeStyle === 'glow';
                      const isOutline = theme.badgeStyle === 'outline';
                      const useImage = !!badge.imageUrl;
                      
                      let IconComponent = null;
                      if (!useImage) {
                        IconComponent = (LucideIcons as any)[badge.iconName || 'Shield'] || LucideIcons.Shield;
                      }

                      return (
                        <motion.div
                          layout
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.8, y: -20 }}
                          transition={{ type: "spring", stiffness: 300, damping: 25 }}
                          whileHover={{ scale: 1.05 }}
                          whileDrag={{ scale: 1.1, zIndex: 10 }}
                          key={badge.id}
                          draggable={!!onReorderBadges}
                          onDragStart={(e: any) => handleDragStart(e, badge.id)}
                          onDragOver={handleDragOver}
                          onDrop={(e: any) => handleDrop(e, badge.id)}
                          onDragEnd={handleDragEnd}
                          className={cn(
                            'flex items-center rounded-full font-bold transition-colors  max-w-full',
                            badgeStyles.containerGap === 'gap-3' ? 'gap-2.5' : (badgeStyles.containerGap === 'gap-1' ? 'gap-1' : 'gap-2'),
                            badgeStyles.padding,
                            badgeStyles.textSize,
                            !!onReorderBadges && 'cursor-grab active:cursor-grabbing',
                            draggedBadgeId === badge.id && 'opacity-50',
                            isOutline ? 'border-[2px] border-current bg-transparent' : 'text-white shadow-sm',
                            (!isOutline && badge.color) ? badge.color : (!isOutline && !badge.color ? 'bg-zinc-800 border border-white/10' : ''),
                            isGlow && 'shadow-[0_0_30px_rgba(255,255,255,0.3)] border border-white/20'
                          )}
                        >
                          {useImage ? (
                            <img src={badge.imageUrl} alt={badge.name} className={cn("object-contain drop-shadow-md", badgeStyles.imgSize)} crossOrigin="anonymous" />
                          ) : (
                            IconComponent && <IconComponent className={badgeStyles.iconSize} />
                          )}
                          <span className="block overflow-hidden text-ellipsis whitespace-nowrap max-w-[300px]">{badge.name}</span>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            ) : (
              <div className={cn(
                "flex-1 flex flex-col w-full",
                bannerSize === 'horizontal' ? "justify-between" : "justify-end gap-8 pb-12"
              )}>
                {/* User Info */}
                <div className={cn("max-w-[1100px] shrink-0 flex", bannerSize === 'vertical' ? "flex-col items-start gap-6" : "items-center gap-6")}>
                  {showProfilePic && profilePicSrc && (
                    <div className={cn("rounded-full overflow-hidden border-4 border-white/20 shadow-2xl shrink-0", 
                      bannerSize === 'horizontal' ? "w-28 h-28" : bannerSize === 'square' ? "w-40 h-40" : "w-48 h-48"
                    )}>
                      <img src={profilePicSrc} alt="Profile" className="w-full h-full object-cover" crossOrigin="anonymous" />
                    </div>
                  )}
                  <div className="flex flex-col">
                    <h1 className={cn(
                      "leading-[1.1] font-black tracking-tighter mb-2 drop-shadow-xl",
                      bannerSize === 'horizontal' ? (showProfilePic ? "text-[56px]" : "text-[72px]") : bannerSize === 'square' ? "text-[80px]" : "text-[100px]"
                    )} style={{ fontFamily: `var(--font-${fontFamily})` }}>
                      {userName || 'Your Name'}
                    </h1>
                    <p className={cn(
                      "font-medium opacity-90 leading-snug drop-shadow-lg max-w-[800px]",
                      bannerSize === 'horizontal' ? (showProfilePic ? "text-[24px]" : "text-[32px]") : bannerSize === 'square' ? "text-[32px]" : "text-[40px]"
                    )} style={{ fontFamily: `var(--font-${fontFamily === 'display' ? 'sans' : fontFamily})` }}>
                      {userTitle || 'Cloud Learner & Technologist'}
                    </p>
                    
                    {/* Social Links Compact */}
                    {showSocials && socialHandles && (
                      <div className={cn(
                        "flex items-center",
                        bannerSize === 'horizontal' ? "gap-6 mt-4" : "gap-8 mt-6"
                      )}>
                        {socialHandles.linkedin && (
                          <div className={cn("flex items-center font-medium opacity-90 drop-shadow-md", bannerSize === 'horizontal' ? "gap-2 text-xl" : "gap-4 text-3xl")}>
                            <LucideIcons.Linkedin className={bannerSize === 'horizontal' ? "w-5 h-5" : "w-10 h-10"} />
                            <span>{socialHandles.linkedin}</span>
                          </div>
                        )}
                        {socialHandles.github && (
                          <div className={cn("flex items-center font-medium opacity-90 drop-shadow-md", bannerSize === 'horizontal' ? "gap-2 text-xl" : "gap-4 text-3xl")}>
                            <LucideIcons.Github className={bannerSize === 'horizontal' ? "w-5 h-5" : "w-10 h-10"} />
                            <span>{socialHandles.github}</span>
                          </div>
                        )}
                        {socialHandles.twitter && (
                          <div className={cn("flex items-center font-medium opacity-90 drop-shadow-md", bannerSize === 'horizontal' ? "gap-2 text-xl" : "gap-4 text-3xl")}>
                            <LucideIcons.Twitter className={bannerSize === 'horizontal' ? "w-5 h-5" : "w-10 h-10"} />
                            <span>{socialHandles.twitter}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Badges */}
                <div className={cn(
                  "flex shrink",
                  bannerSize === 'horizontal' ? "justify-end mt-auto pt-2" : "justify-start mt-8"
                )}>
                  <div className={cn(
                    "flex items-center flex-wrap-reverse",
                    bannerSize === 'horizontal' ? "justify-end max-w-[95%]" : "justify-start max-w-full",
                    badgeStyles.containerGap
                  )}>
                    {selectedBadges.length === 0 && (
                      <div className="text-2xl opacity-50 italic font-medium">Select badges to display here...</div>
                    )}
                    
                    <AnimatePresence>
                      {selectedBadges.map((badge) => {
                        const isGlow = theme.badgeStyle === 'glow';
                        const isOutline = theme.badgeStyle === 'outline';
                        const useImage = !!badge.imageUrl;
                        
                        let IconComponent = null;
                        if (!useImage) {
                          IconComponent = (LucideIcons as any)[badge.iconName || 'Shield'] || LucideIcons.Shield;
                        }

                        return (
                          <motion.div
                            layout
                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.8, y: -20 }}
                            transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            whileHover={{ scale: 1.05 }}
                            whileDrag={{ scale: 1.1, zIndex: 10 }}
                            key={badge.id}
                            draggable={!!onReorderBadges}
                            onDragStart={(e: any) => handleDragStart(e, badge.id)}
                            onDragOver={handleDragOver}
                            onDrop={(e: any) => handleDrop(e, badge.id)}
                            onDragEnd={handleDragEnd}
                            className={cn(
                              'flex items-center rounded-full font-bold transition-colors  max-w-full',
                              badgeStyles.containerGap === 'gap-3' ? 'gap-2.5' : (badgeStyles.containerGap === 'gap-1' ? 'gap-1' : 'gap-2'),
                              badgeStyles.padding,
                              badgeStyles.textSize,
                              !!onReorderBadges && 'cursor-grab active:cursor-grabbing',
                              draggedBadgeId === badge.id && 'opacity-50',
                              isOutline ? 'border-[2px] border-current bg-transparent' : 'text-white shadow-sm',
                              (!isOutline && badge.color) ? badge.color : (!isOutline && !badge.color ? 'bg-zinc-800 border border-white/10' : ''),
                              isGlow && 'shadow-[0_0_30px_rgba(255,255,255,0.3)] border border-white/20'
                            )}
                          >
                            {useImage ? (
                              <img src={badge.imageUrl} alt={badge.name} className={cn("object-contain drop-shadow-md", badgeStyles.imgSize)} crossOrigin="anonymous" />
                            ) : (
                              IconComponent && <IconComponent className={badgeStyles.iconSize} />
                            )}
                            <span className="block overflow-hidden text-ellipsis whitespace-nowrap max-w-[300px]">{badge.name}</span>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    );
  }
);

BannerPreview.displayName = 'BannerPreview';
